// Example.tsx
import { makeStyles } from "@fluentui/react-components";
import { tokens } from "../../theme";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    lineHeightBase300: tokens.primitives["lineHeight-Base-300"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],

    // Colors
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralSecondary: tokens.semantic["neutral-Secondary"],
    neutralWhite: tokens.semantic["neutral-White"],
    neutralLighter: tokens.semantic["neutral-Lighter"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
    themePrimary: tokens.palette["theme-Primary"],
    themeLighter: tokens.palette["theme-Lighter"],
    neutralPrimaryBackground: tokens.semantic["neutralPrimaryBackground"],
    neutralPrimaryBackgroundPressed: tokens.semantic["neutralPrimaryBackgroundPressed"],

    // Radius
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusXLarge: tokens.primitives["stroke-Radius-XLarge"],

    // Shadows
    shadowRestingSmall: tokens.shadows["shadow-Resting-Small"],
    shadowRestingMedium: tokens.shadows["shadow-Resting-Medium"],

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    card: {
        display: "flex",
        alignItems: "flex-start",
        backgroundColor: t.neutralWhite,
        border: `1px solid ${t.neutralLighter}`,
        borderRadius: t.radiusXLarge,
        padding: `${t.spacingVerticalM} ${t.spacingHorizontalL}`,
        cursor: "pointer",
        textAlign: "left",
        transitionProperty: "border-color, box-shadow, background-color",
        transitionDuration: "0.15s",
        width: "100%",
        boxSizing: "border-box",
        minHeight: "80px",

        "&:hover": {
            backgroundColor: t.neutralPrimaryBackground,
            border: `1px solid ${t.themePrimary}`,
            boxShadow: t.shadowRestingSmall
        },

        "&:active": {
            backgroundColor: t.neutralPrimaryBackgroundPressed,
            border: `1px solid ${t.themePrimary}`
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    cardText: {
        margin: 0,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        color: t.neutralSecondary
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

interface Props {
    text: string;
    value: string;
    onClick: (value: string) => void;
}

export const Example = ({ text, value, onClick }: Props) => {
    const styles = useStyles();

    return (
        <button className={styles.card} onClick={() => onClick(value)} type="button">
            <span className={styles.cardText}>{text}</span>
        </button>
    );
};
